import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { FlexColumnContainer, FlexRowContainer } from "@trailhead/core-ui";
import {
  Button as AsurionButton,
  Text,
  breakpoints,
  colors,
} from "@trailhead/asurion-ui";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { trackEvent } from "@trailhead/analytics";
import {
  ExpertSupportContentContainer,
  createExpertSupport,
  useContentfulData,
} from "@trailhead/contentful";
import { useMxContext } from "@trailhead/mx-context";
import { useQueryParams } from "@trailhead/smart-setup-core";

import { ExpertSupportProps } from "./propTypes";
import { useChatClient } from "./useChatClient";

const MiniExpertSupportCTAContainer = styled(FlexRowContainer)`
  background-color: ${colors.neutralBrightest};
  max-width: 20.875rem;
  margin: 5rem auto !important; //overrides the declared margin-top value in TutorialComposerStep component
  justify-content: center;
  align-items: center;
  border-radius: 2.1875rem;
  gap: 0.75rem;
  padding: 0.8125rem 1.25rem 1.0625rem 1.25rem;
`;

const ProfileImageContainer = styled(FlexColumnContainer)`
  padding-bottom: 1.75rem;
  flex: 1 1 15%;
`;

const ContentContainer = styled(FlexColumnContainer)`
  padding: 0.6875rem 0rem 0rem;
  display: inline-flex;
  flex: 1 1 85%;
  gap: 0.4375rem;

  ${breakpoints.md} {
    padding: 0.6875rem 0.9375rem 0rem 0rem;
  }
`;

const ImageContainer = styled.div``;

const HeaderContainer = styled(FlexColumnContainer)``;
const HeaderTitle = styled(Text)``;

const HeaderSubtext = styled(Text)`
  display: inline-flex;
`;

const ButtonContainer = styled(FlexColumnContainer)``;
const ButtonWrapper = styled(AsurionButton)`
  width: 11.0625rem;
  height: 2rem;
  padding: 0.25rem 0.75rem;
`;

// TODO: Only present in Verizon Smart Setup design. Since the long term plan is to apply the
// said design to non-smart setup as well, might need to apply the changes here to QuestionsCTA, since it's
// the component used for all guides.
export const MiniExpertSupportCTA: React.FC<ExpertSupportProps> = ({
  content = {
    title: "Stuck on this step?",
    subtext: "I can help.",
    primaryButtonText: "Start a live chat",
    image: undefined,
  },
}) => {
  const t = useSanitizedTranslation();
  const queryParams = useQueryParams();
  const { client, language } = useMxContext();
  const ChatClient = useChatClient();
  const openChat = (): void => {
    trackEvent("MiniExpertSupportCTA_Chat_Click");
    ChatClient.openMessagingOverlay();
  };

  // TODO: Eventually will be removed (line 89 - 104) since the content will be coming directly from factory,
  // just like QuestionsCTA component
  const [ctaContent, setCtaContent] =
    useState<ExpertSupportProps["content"]>(content);
  const { isLoading, data } = useContentfulData<ExpertSupportContentContainer>(
    "expertSupportContentCard",
    client,
    language,
    queryParams.get("isPreview"),
    { "fields.id": "expertSupportVerizonSmartSetupMiniCTA" },
  );

  useEffect(() => {
    if (!isLoading) {
      setCtaContent(createExpertSupport(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <MiniExpertSupportCTAContainer>
      <ProfileImageContainer>
        {ctaContent?.image ? (
          <ImageContainer>{ctaContent.image}</ImageContainer>
        ) : null}
      </ProfileImageContainer>
      <ContentContainer>
        <HeaderContainer>
          <HeaderTitle>
            <Text
              weight="heavy"
              dangerouslySetInnerHTML={{
                __html: t(ctaContent?.title as string),
              }}
            />{" "}
            <HeaderSubtext
              dangerouslySetInnerHTML={{
                __html: t(ctaContent?.subtext as string),
              }}
            />
          </HeaderTitle>
        </HeaderContainer>
        <ButtonContainer>
          <ButtonWrapper
            iconSide="right"
            iconSrc="ArrowRight"
            onClick={openChat}
          >
            <Text
              dangerouslySetInnerHTML={{
                __html: t(ctaContent?.primaryButtonText as string),
              }}
            />
          </ButtonWrapper>
        </ButtonContainer>
      </ContentContainer>
    </MiniExpertSupportCTAContainer>
  );
};
